import { Button, Checkbox, Flex, Form, Input, Select, Typography } from "antd";
import clsx from "clsx";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";

import { QueryErrorAlert } from "design-system/components/QueryErrorAlert";
import { useScreenSize } from "design-system/hooks";
import { useSandboxAuth } from "sandbox/contexts/SandboxAuthContext";
import { ThemeToggle } from "sandbox/features/shared/ThemeToggle";
import { useSandboxLogo } from "sandbox/hooks";
import { sandboxPaths } from "sandbox/paths";
import { checkEmailDomain } from "sandbox/utils/emailValidation";

import { SALESFORCE_COUNTRIES } from "../../constants";
import { toCountryOptions } from "../../hooks/serializers";
import s from "./AuthScreen.module.css";
import { VerifyEmailScreen } from "./VerifyEmailScreen";

type Mode = "signin" | "signup" | "verify";

interface SignInValues {
  email: string;
  password: string;
}

interface SignUpValues {
  agreedToTerms: boolean;
  company: string;
  confirmPassword: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  country: string;
  marketingConsent?: boolean;
  password: string;
  role?: string;
}

export const AuthScreen = () => {
  const { t } = useTranslation(["sandbox", "common"]);
  const [mode, setMode] = useState<Mode>("signin");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const { signIn, signUp, signUpSalesforceReq } = useSandboxAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const size = useScreenSize();
  const [form] = Form.useForm();
  const logo = useSandboxLogo();

  const showHero = !["xs", "sm"].includes(size);
  const isSignIn = mode === "signin";

  const getButtonLabel = (loading: boolean, isSignInMode: boolean) => {
    if (loading)
      return isSignInMode ? t("actions.signingIn") : t("auth.createWorkspace");
    return isSignInMode
      ? t("actions.signIn")
      : t("actions.createSandboxAccount");
  };

  const onFinish = async (values: SignInValues | SignUpValues) => {
    setSubmitting(true);
    setError("");
    try {
      if (isSignIn) {
        const v = values as SignInValues;
        await signIn({ email: v.email, password: v.password });
        const from = (location.state as { from?: string } | null)?.from;
        navigate(from || sandboxPaths.explorer, { replace: true });
      } else {
        const v = values as SignUpValues;
        const commonSignUpData = {
          country: v.country,
          company: v.company,
          email: v.email,
          password: v.password,
          role: v.role,
          agreedToTerms: v.agreedToTerms,
          marketingConsent: v.marketingConsent,
        };

        const signUpData = {
          ...commonSignUpData,
          firstName: v.firstName,
          lastName: v.lastName,
        };

        await signUp(signUpData);
        void signUpSalesforceReq({
          ...signUpData,
          firstName: v.firstName ?? "",
          lastName: v.lastName ?? "",
        });

        setVerifiedEmail(v.email);
        setMode("verify");
      }
    } catch (e) {
      setError(
        (e as { message?: string })?.message ?? "An unexpected error occurred",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === "verify") {
    return <VerifyEmailScreen email={verifiedEmail} />;
  }

  return (
    <Flex vertical className={s.page}>
      <header className={s.header}>
        <img src={logo} alt="Thredd" className={s.logo} />
        <span className={s.brandPill}>{t("components.header.brandPill")}</span>
        <ThemeToggle className={s.themeToggle} />
      </header>
      <Flex flex={1} align="center" justify="center" className={s.body}>
        <Flex vertical align="center" justify="center" className={s.content}>
          <Flex className={s.card}>
            <Flex vertical className={s.formContent}>
              <Typography.Title level={3} className={s.heading}>
                {isSignIn ? t("auth.title.signIn") : t("auth.title.signUp")}
              </Typography.Title>
              <Typography.Paragraph className={s.subtitle}>
                {isSignIn
                  ? t("auth.subtitle.signIn")
                  : t("auth.subtitle.signUp")}
              </Typography.Paragraph>

              {error && (
                <div className={s.errorAlert}>
                  <QueryErrorAlert description={error} />
                </div>
              )}

              <Form
                key={mode}
                form={form}
                layout="vertical"
                onFinish={onFinish}
                requiredMark={false}
                autoComplete="on"
              >
                {!isSignIn && (
                  <>
                    <Form.Item
                      name="firstName"
                      label={t("common:labels.firstName")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            field: t("common:labels.firstName"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t("placeholders.firstName")}
                      />
                    </Form.Item>
                    <Form.Item
                      name="lastName"
                      label={t("common:labels.lastName")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            field: t("common:labels.lastName"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t("placeholders.lastName")}
                      />
                    </Form.Item>

                    <Form.Item
                      name="country"
                      label={t("common:components.addressFields.country")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            field: t("common:components.addressFields.country"),
                          }),
                        },
                      ]}
                    >
                      <Select
                        placeholder={t("placeholders.country")}
                        data-testid="sign-in-country"
                        showSearch={{
                          filterOption: (input, option) =>
                            (option?.label?.toLowerCase() ?? "").includes(
                              input.toLowerCase(),
                            ),
                        }}
                        options={toCountryOptions(SALESFORCE_COUNTRIES)}
                      />
                    </Form.Item>
                    <Form.Item
                      name="role"
                      label={t("common:labels.role")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            field: t("common:labels.role"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t("placeholders.role")}
                      />
                    </Form.Item>
                    <Form.Item
                      name="company"
                      label={t("labels.company")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            field: t("labels.company"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t("placeholders.analyticalEngines")}
                      />
                    </Form.Item>
                  </>
                )}

                <Form.Item
                  name="email"
                  label={t("labels.workEmail")}
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        field: t("labels.workEmail"),
                      }),
                    },
                    { type: "email", message: t("validation.emailType") },
                    {
                      validator: (_, value: string | undefined) => {
                        if (!value) return Promise.resolve();
                        const result = checkEmailDomain(value);
                        return result.code === "ok"
                          ? Promise.resolve()
                          : Promise.reject(new Error(result.message));
                      },
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder={t("placeholders.workEmail")}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={t("labels.password")}
                  tooltip={
                    isSignIn
                      ? t("tooltips.password")
                      : t("validation.passwordExtra")
                  }
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        field: t("labels.password"),
                      }),
                    },
                    {
                      min: 8,
                      message: t("validation.minChars", { count: 8 }),
                    },
                    {
                      pattern: /\d/,
                      message: t("validation.mustIncludeNumber"),
                    },
                    {
                      pattern: /[A-Z]/,
                      message: t("validation.mustIncludeUppercase"),
                    },
                    {
                      pattern: /[a-z]/,
                      message: t("validation.mustIncludeLowercase"),
                    },
                  ]}
                >
                  <Input.Password
                    size="large"
                    placeholder={t("placeholders.password")}
                  />
                </Form.Item>

                {!isSignIn && (
                  <Form.Item
                    name="confirmPassword"
                    label={t("labels.confirmPassword")}
                    required
                    tooltip={t("tooltips.confirmPassword")}
                    dependencies={["password"]}
                    rules={[
                      {
                        required: true,
                        message: t("validation.required", {
                          field: t("labels.confirmPassword"),
                        }),
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(t("validation.passwordsDontMatch")),
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      size="large"
                      placeholder={t("placeholders.password")}
                    />
                  </Form.Item>
                )}

                {!isSignIn && (
                  <>
                    <Form.Item
                      name="agreedToTerms"
                      valuePropName="checked"
                      className={s.consentCheckbox}
                      rules={[
                        {
                          validator: (_, value: boolean | undefined) =>
                            value
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error(t("validation.mustAgreeToTerms")),
                                ),
                        },
                      ]}
                    >
                      <Checkbox>
                        <Trans
                          i18nKey="components.authScreen.agreeToTerms"
                          t={t}
                          components={{
                            termsLink: (
                              <Link
                                to={sandboxPaths.terms}
                                target="_blank"
                                rel="noreferrer"
                                className={s.termsLink}
                              />
                            ),
                          }}
                        />
                      </Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="marketingConsent"
                      valuePropName="checked"
                      className={s.consentCheckbox}
                    >
                      <Checkbox>
                        {t("components.authScreen.marketingConsent")}
                      </Checkbox>
                    </Form.Item>
                  </>
                )}

                {isSignIn && (
                  <Flex
                    justify="flex-end"
                    align="center"
                    className={s.rowBetween}
                  >
                    <Link
                      to={sandboxPaths.forgotPassword}
                      className={s.linkButton}
                    >
                      {t("actions.forgotPassword")}
                    </Link>
                  </Flex>
                )}

                <Form.Item shouldUpdate noStyle>
                  {() => {
                    const requiredSignUpFields = [
                      "firstName",
                      "lastName",
                      "country",
                      "company",
                      "role",
                      "email",
                      "password",
                      "confirmPassword",
                      "agreedToTerms",
                    ];
                    const isSignUpIncomplete =
                      !isSignIn &&
                      (requiredSignUpFields.some(
                        field => !form.getFieldValue(field),
                      ) ||
                        form
                          .getFieldsError(requiredSignUpFields)
                          .some(({ errors }) => errors.length > 0));

                    return (
                      <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        block
                        loading={submitting}
                        disabled={isSignUpIncomplete}
                        className={s.submit}
                      >
                        {getButtonLabel(submitting, isSignIn)}
                      </Button>
                    );
                  }}
                </Form.Item>
              </Form>

              <Flex
                justify="center"
                align="center"
                gap="small"
                className={s.footer}
              >
                <Typography.Text className={s.footerText}>
                  {isSignIn
                    ? t("auth.newToThredd")
                    : t("auth.alreadyHaveAccount")}
                </Typography.Text>
                <Button
                  type="link"
                  onClick={() => setMode(isSignIn ? "signup" : "signin")}
                  className={s.toggle}
                >
                  {isSignIn
                    ? t("components.authScreen.createAccountLink")
                    : t("actions.signIn")}
                </Button>
              </Flex>
            </Flex>

            {showHero && (
              <Flex
                align={isSignIn ? "center" : "flex-start"}
                justify="center"
                className={clsx(s.heroPanel, !isSignIn && s.heroPanelSignUp)}
              >
                <img
                  src="/images/cardCreatedSuccessfully.png"
                  alt=""
                  className={s.heroImage}
                />
              </Flex>
            )}
          </Flex>
          <p className={s.fineprint}>{t("auth.fineprint")}</p>
        </Flex>
      </Flex>
    </Flex>
  );
};
